import { Alert, Descriptions, Form, Input, Modal, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatMoneyValue } from '@/shared/lib/money'
import { formatDateTime } from '@/shared/lib/format'
import { cancelPayment, CANCEL_REASON_MAX, CANCEL_REASON_MIN, type Payment } from './paymentsApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface Props {
  open: boolean
  payment: Payment | null
  onClose: () => void
}

interface FormValues {
  reason: string
  confirm: string
}

/**
 * To'lovni bekor qilish — MOLIYAVIY va QAYTARIB BO'LMAYDIGAN amal.
 *
 * ⚠️ Uch qorovul, uchtasi ham ataylab:
 *
 * 1. **Oqibat PROZA bilan yozilgan**, "Ishonchingiz komilmi?" emas.
 *    Operator nima bo'lishini bilishi kerak: jurnalga teskari yozuv
 *    tushadi va abonent balansi kamayadi.
 * 2. **Sabab MAJBURIY.** `ledger` o'zgarmas — izohni keyin qo'shib
 *    bo'lmaydi, ya'ni "bu nima edi?" savoli abadiy javobsiz qoladi.
 * 3. **Login terilishi talab qilinadi** (`NasDeleteModal` naqshi).
 *    Ro'yxatdan noto'g'ri qatorni bosish oson; login terish esa
 *    "aynan shu abonentning to'lovi" degan tasdiq.
 *
 * ⚠️ "Orqaga qaytarish" tugmasi YO'Q va bo'lmaydi: `ledger.entry` ustida
 * `DO INSTEAD NOTHING`, `ledger.txn` ustida `reject_mutation()` turibdi.
 * Teskari yozuvni "bekor qilish" — bu yana bir yozuv, ya'ni jurnal
 * uzunlashadi, holat esa tiklanmaydi.
 */
export function CancelPaymentModal({ open, payment, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  // ⚠️ IKKALA prefiks: bekor qilish `ledger` jurnaliga ham yozadi.
  const runner = useOperationRunner([queryKeys.payments.all, queryKeys.ledger.accounts])

  const typed = Form.useWatch('confirm', form)
  const confirmed = payment !== null && typed?.trim() === payment.account

  const close = () => {
    form.resetFields()
    runner.clear()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('payments.cancel.title')}
      okText={t('payments.cancel.confirm')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true, disabled: !confirmed }}
      confirmLoading={runner.running}
      onCancel={close}
      destroyOnHidden
      onOk={() =>
        submitOnValid(form, async (values) => {
          if (payment === null) return
          const outcome = await runner.run(() =>
            cancelPayment(payment.id, values.reason.trim()),
          )
          if (outcome.status !== 'FAILED') {
            form.resetFields()
          }
        })
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={t('payments.cancel.warnTitle')}
          description={t('payments.cancel.warnText')}
        />

        {payment ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t('payments.columns.account')}>
              {payment.account}
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.amount')}>
              {formatMoneyValue(payment.amount)}
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.channel')}>
              {payment.channelName}
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.createdAt')}>
              {formatDateTime(payment.createdAt)}
            </Descriptions.Item>
          </Descriptions>
        ) : null}

        {runner.result ? <OperationResultAlert result={runner.result} /> : null}

        <Form form={form} layout="vertical" requiredMark>
          <Form.Item
            name="reason"
            label={t('payments.cancel.reason')}
            extra={t('payments.cancel.reasonHint')}
            rules={[
              { required: true, message: t('payments.cancel.reasonRequired') },
              { min: CANCEL_REASON_MIN, message: t('payments.cancel.reasonRequired') },
              { max: CANCEL_REASON_MAX, message: t('payments.cancel.reasonTooLong') },
            ]}
          >
            <Input.TextArea rows={3} maxLength={CANCEL_REASON_MAX} showCount />
          </Form.Item>

          <Form.Item
            name="confirm"
            label={t('payments.cancel.typeToConfirmLabel')}
            rules={[{ required: true, message: t('payments.cancel.typeToConfirmRequired') }]}
          >
            <Input autoComplete="off" placeholder={payment?.account ?? ''} />
          </Form.Item>

          <Typography.Text type="secondary">
            {t('actions.typeToConfirm', { value: payment?.account ?? '' })}
          </Typography.Text>
        </Form>
      </Space>
    </Modal>
  )
}
